import { z } from 'zod';
export const Uuid = z.uuid();
/** UTC timestamp as ISO 8601 with offset (the server always sends `Z`). */
export const IsoDateTime = z.iso.datetime({ offset: true });
/** Stored lower-cased and trimmed. */
export const Email = z
    .string()
    .trim()
    .max(254)
    .pipe(z.email())
    .transform((s) => s.toLowerCase());
export const Slug = z
    .string()
    .min(2)
    .max(40)
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'slug');
/** Addresses the product itself uses (portal, link pages, display, mail, ...): no tenant may take them as a slug. */
export const RESERVED_SLUGS = new Set([
    'app', 'go', 'display', 'test', 'www', 'api', 'mail', 'status', 'admin', 'platform', 'static', 'assets', 'cdn',
    'login', 'sso', 'auth', 'support', 'help', 'billing', 'portal', 'account', 'cloudsign',
]);
/** Hex colour like #1a2b3c. */
export const HexColour = z.string().regex(/^#[0-9a-fA-F]{6}$/);
/** BCP 47 locale tag. Only en-GB ships in Phase 0. */
export const Locale = z.enum(['en-GB']);
/** IANA time zone name, e.g. Europe/London. Validated loosely here; the server checks with luxon. */
export const TimeZone = z.string().min(1).max(64);
export const PageQuery = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
    search: z.string().trim().max(200).optional(),
    sort: z.string().max(64).optional(),
    order: z.enum(['asc', 'desc']).default('asc'),
});
/** Wraps an item schema in the standard paged list response. */
export const page = (item) => z.object({
    items: z.array(item),
    total: z.number().int().min(0),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
});
/**
 * Error codes. HTTP status mapping:
 * validation 400, unauthenticated 401, mfa_required 401, forbidden 403, not_found 404,
 * conflict 409, rate_limited 429, module_disabled 403, tenant_suspended 403, internal 500.
 * A record in another tenant always gives not_found (never forbidden), so ids don't leak.
 */
export const ErrorCode = z.enum([
    'validation',
    'unauthenticated',
    'mfa_required',
    'forbidden',
    'not_found',
    'conflict',
    'rate_limited',
    'module_disabled',
    'tenant_suspended',
    'internal',
    // Phase 4 (ADR 0031): billing hold turned the portal read-only (423).
    'tenant_read_only',
]);
export const ApiError = z.object({
    error: z.object({
        code: ErrorCode,
        /** i18n key, e.g. `error.forbidden`. The client translates it. */
        messageKey: z.string(),
        params: z.record(z.string(), z.union([z.string(), z.number()])).optional(),
        /** Field-level problems for validation errors: path -> i18n key. */
        fields: z.record(z.string(), z.string()).optional(),
        requestId: z.string().optional(),
    }),
});
export const OkResponse = z.object({ ok: z.literal(true) });
// ---------- Phase 2 helpers (architect) ----------
/** A calendar date, `YYYY-MM-DD`. */
export const LocalDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/** A wall-clock time in a site's time zone, `HH:MM` (24 h). */
export const TimeOfDay = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
/** A phone number as typed; the server normalises to E.164 (UK default) before sending SMS. */
export const Phone = z.string().trim().min(5).max(32).regex(/^[+0-9 ()-]+$/);
/**
 * Gives a settings object whose fields all have defaults a default of its own parsed defaults, so nested
 * sections never need their default spelled out twice (`withDefaults(z.object({...}))`).
 */
export const withDefaults = (schema) => schema.default(schema.parse({}));
/**
 * PATCH body from a create schema: every field optional and **no defaults**. `schema.partial()` keeps zod defaults, so a PATCH
 * that sent only `{ status }` would come back with every defaulted field reset (e.g. a webhook's events emptied). Use this instead.
 */
export function patchOf(schema) {
    const shape = {};
    for (const [key, field] of Object.entries(schema.shape)) {
        let inner = field;
        while (['optional', 'default', 'prefault'].includes(inner.def.type))
            inner = inner.def.innerType;
        shape[key] = inner.optional();
    }
    return z.object(shape);
}
//# sourceMappingURL=common.js.map